import React from 'react';
import { useSettings } from '../../hooks';
import { Dropdown, Toggle, InputField } from '../common';
import { CONDUIT_TYPE_DISPLAY, ConduitType, WireSize, TemperatureRating, TEMPERATURE_RATING_DISPLAY, UnitSystem } from '../../engine/types';
import { NEC_EDITIONS, NEC_EDITION_LABELS, isNecEdition, necEditionDifferences, type NecEdition } from '../../engine/tables/necEditions';
import './SettingsPanel.css';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const VOLTAGE_OPTIONS = [
  { value: '480', label: '480V' },
  { value: '240', label: '240V' },
  { value: '208', label: '208V' },
  { value: '120', label: '120V' },
];

const PHASE_OPTIONS = [
  { value: 'three', label: 'Three-Phase' },
  { value: 'single', label: 'Single-Phase' },
];

const MATERIAL_OPTIONS = [
  { value: 'copper', label: 'Copper' },
  { value: 'aluminum', label: 'Aluminum' },
];

const TEMPERATURE_RATING_OPTIONS = Object.entries(TEMPERATURE_RATING_DISPLAY).map(([value, label]) => ({
  value,
  label,
}));

const MAX_CONDUCTOR_OPTIONS = [
  { value: '250', label: '250 kcmil' },
  { value: '350', label: '350 kcmil' },
  { value: '500', label: '500 kcmil' },
  { value: '600', label: '600 kcmil' },
  { value: '750', label: '750 kcmil' },
  { value: '1000', label: '1000 kcmil' },
];

const CONDUIT_TYPE_OPTIONS = Object.entries(CONDUIT_TYPE_DISPLAY).map(([value, label]) => ({
  value,
  label,
}));

const NEC_EDITION_OPTIONS = [...NEC_EDITIONS].reverse().map((edition: NecEdition) => ({
  value: edition,
  label: NEC_EDITION_LABELS[edition],
}));

const NEC_EDITION_DIFFERENCES = necEditionDifferences();

const UNIT_SYSTEM_OPTIONS = [
  { value: 'imperial', label: 'Imperial (ft, in²)' },
  { value: 'metric', label: 'Metric (m, mm²)' },
];

export function SettingsPanel({ isOpen, onClose }: SettingsPanelProps) {
  const {
    settings,
    setVoltage,
    setPhase,
    setMaterial,
    setTemperatureRating,
    setMaxConductorSize,
    setConduitType,
    setAmbientTemperature,
    setConduitFillDerating,
    setConduitOversizeEnabled,
    setConduitOversizePercent,
    setUnitSystem,
    setNecEdition,
    resetSettings,
  } = useSettings();

  if (!isOpen) return null;

  return (
    <>
      <div className="settings-overlay" onClick={onClose} aria-hidden="true" />
      <div className="settings-panel">
        <div className="settings-header">
          <h2>Global Settings</h2>
          <button className="settings-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="settings-content">
          <div className="settings-section">
            <h3>Electrical Defaults</h3>

            <Dropdown
              id="settings-voltage"
              label="Default Voltage"
              value={settings.voltage.toString()}
              options={VOLTAGE_OPTIONS}
              onChange={(val) => setVoltage(parseInt(val, 10))}
            />

            <Dropdown
              id="settings-phase"
              label="Default Phase"
              value={settings.phase}
              options={PHASE_OPTIONS}
              onChange={(val) => setPhase(val as 'single' | 'three')}
            />

            <Dropdown
              id="settings-material"
              label="Conductor Material"
              value={settings.conductorMaterial}
              options={MATERIAL_OPTIONS}
              onChange={(val) => setMaterial(val as 'copper' | 'aluminum')}
            />

            <Dropdown
              id="settings-temp-rating"
              label="Conductor Temperature Rating"
              value={settings.temperatureRating.toString()}
              options={TEMPERATURE_RATING_OPTIONS}
              onChange={(val) => setTemperatureRating(parseInt(val, 10) as TemperatureRating)}
            />

            <Dropdown
              id="settings-nec-edition"
              label="NEC code edition"
              value={settings.necEdition}
              options={NEC_EDITION_OPTIONS}
              onChange={(val) => {
                if (isNecEdition(val)) setNecEdition(val);
              }}
            />
            <div className="settings-info" data-testid="nec-edition-differences">
              <p><strong>What this changes</strong></p>
              {NEC_EDITION_DIFFERENCES.map((item) => (
                <p key={item.topic} className="settings-note">
                  <strong>{item.topic}:</strong> {item.note}
                </p>
              ))}
            </div>
          </div>

          <div className="settings-section">
            <h3>Display Settings</h3>

            <Dropdown
              id="settings-unit-system"
              label="Unit System"
              value={settings.unitSystem}
              options={UNIT_SYSTEM_OPTIONS}
              onChange={(val) => setUnitSystem(val as UnitSystem)}
            />
            <p className="settings-note">
              Note: NEC calculations use imperial units internally. Metric values are conversions for display only.
            </p>
          </div>

          <div className="settings-section">
            <h3>Conductor & Conduit</h3>

            <Dropdown
              id="settings-max-conductor"
              label="Max Conductor Size"
              value={settings.maxConductorSize.toString()}
              options={MAX_CONDUCTOR_OPTIONS}
              onChange={(val) => setMaxConductorSize(parseInt(val, 10) as WireSize)}
            />

            <Dropdown
              id="settings-conduit-type"
              label="Conduit Type"
              value={settings.conduitType}
              options={CONDUIT_TYPE_OPTIONS}
              onChange={(val) => setConduitType(val as ConduitType)}
            />

            <Toggle
              id="settings-conduit-oversize"
              label="Oversize Conduit"
              checked={settings.conduitOversizeEnabled}
              onChange={setConduitOversizeEnabled}
            />

            {settings.conduitOversizeEnabled && (
              <InputField
                id="settings-oversize-percent"
                label="Oversize Percentage"
                type="number"
                value={settings.conduitOversizePercent}
                onChange={(val) => {
                  const parsed = parseInt(val, 10);
                  if (!isNaN(parsed)) {
                    setConduitOversizePercent(Math.max(0, Math.min(100, parsed)));
                  }
                }}
                min={0}
                max={100}
                unit="%"
              />
            )}
          </div>

          <div className="settings-section">
            <h3>Derating Factors</h3>

            <InputField
              id="settings-ambient-temp"
              label="Ambient Temperature"
              type="number"
              value={settings.ambientTemperature}
              onChange={(val) => setAmbientTemperature(parseInt(val, 10) || 30)}
              min={-40}
              max={60}
              unit="°C"
            />

            <Toggle
              id="settings-conduit-fill"
              label="Apply Conduit Fill Derating"
              checked={settings.conduitFillDerating}
              onChange={setConduitFillDerating}
            />
          </div>

          <div className="settings-section">
            <h3>Company Standards</h3>
            <div className="settings-info">
              <p><strong>Minimum Conductor:</strong> #12 AWG</p>
              <p><strong>Minimum Conduit:</strong> 1"</p>
              <p className="settings-note">
                These minimums are automatically applied to all calculations.
              </p>
            </div>
          </div>
        </div>

        <div className="settings-footer">
          <button className="btn-secondary" onClick={resetSettings}>
            Reset to Defaults
          </button>
          <button className="btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </>
  );
}

export default SettingsPanel;
